/**
 * Shared math for KPI cards rendered on multiple surfaces (Overview,
 * Financials, etc.). Lives in `utils/` rather than being inlined in
 * each component so the formula has a single source of truth — per
 * AGENTS.md §5 (DRY for shared knowledge).
 */

import { AppStrings } from "./constants/AppStrings";

/**
 * Title for the withdrawal/deposit % card. Sweepstakes clients use
 * "REDEMPTION %"; everyone else uses "WITHDRAWN %".
 */
export function getRedeemabilityPctTitle(isSweepstakes: boolean): string {
  return isSweepstakes
    ? AppStrings.OVERVIEW_REDEEMABILITY_PCT
    : AppStrings.PAM_WITHDRAWN_PCT;
}

/**
 * Cards shipped by the BE all share this `{value, previous_value,
 * percent_change}` envelope so the percent-change badge has a
 * period-over-period delta to render. Frontend-derived cards (e.g.
 * `redeemability_pct` below) need to mirror this shape so the same
 * `KpiCard` render path applies without a special case.
 */
export type KpiCardEnvelope = {
  value: number;
  previous_value: number;
  percent_change: number;
};

/**
 * (current - previous) / |previous| * 100. Returns 0 when `previous`
 * is 0 to avoid `Infinity` / `NaN` rendering — the badge stays neutral
 * instead of showing a misleading 100% jump from a zero baseline.
 */
export function calculatePercentChange(
  value: number,
  previousValue: number,
): number {
  if (previousValue === 0) {
    return 0;
  }
  return ((value - previousValue) / Math.abs(previousValue)) * 100;
}

/**
 * BE-card-shaped subset accepted by `computeRedeemabilityCard`. Only
 * `deposit_amount` and `withdrawal_amount` are read — narrowing here
 * (instead of importing `FinancialsKpiCardsResponse`) keeps the helper
 * usable from any KPI surface that exposes these two fields.
 */
type RedeemabilityInput = {
  deposit_amount?: Pick<KpiCardEnvelope, "value" | "previous_value"> | null;
  withdrawal_amount?: Pick<KpiCardEnvelope, "value" | "previous_value"> | null;
};

/**
 * Computes `redeemability = (withdrawal / deposit) * 100` for both the
 * current and prior periods. Returns the full BE envelope so consumers
 * can render it through the standard `KpiCard` path. Zero-deposit
 * periods short-circuit to 0% (no `Infinity` / `NaN`).
 *
 * Used by:
 *   - FinancialsV2 (Financials KPI tab — last card)
 *   - OverviewV2 (Overview KPI tab — Financials section)
 */
export function computeRedeemabilityCard(
  data?: RedeemabilityInput | null,
): KpiCardEnvelope {
  const depositValue = Number(data?.deposit_amount?.value ?? 0);
  const withdrawalValue = Number(data?.withdrawal_amount?.value ?? 0);
  const value = depositValue !== 0 ? (withdrawalValue / depositValue) * 100 : 0;

  const previousDeposit = Number(data?.deposit_amount?.previous_value ?? 0);
  const previousWithdrawal = Number(
    data?.withdrawal_amount?.previous_value ?? 0,
  );
  const previousValue =
    previousDeposit !== 0 ? (previousWithdrawal / previousDeposit) * 100 : 0;

  return {
    value,
    previous_value: previousValue,
    percent_change: calculatePercentChange(value, previousValue),
  };
}
